import { createAuthClient, kBroadcastChannel } from 'better-auth/client';
import { organizationClient, twoFactorClient } from 'better-auth/client/plugins';
import { criarBuscador, type Buscador } from './api/cliente';

/**
 * O cliente do Better Auth avisa as outras abas gravando `better-auth.message` no `localStorage`.
 * O painel não grava nada de sessão no navegador (constituição III.1, spec 002 CA-07): o canal é
 * trocado por um que não faz nada. A sessão de cada aba é o `/me` do TanStack Query, e um 401
 * leva ao login pelo tratamento central.
 */
function silenciarAvisoEntreAbas(): void {
  const global = globalThis as Record<symbol, unknown>;
  global[kBroadcastChannel] = {
    subscribe: () => () => undefined,
    post: () => undefined,
    setup: () => () => undefined,
  };
}

/**
 * Cliente oficial do Better Auth (ADR-0007): sessão em cookie `httpOnly` no mesmo domínio; o
 * painel nunca vê o token. Usa o mesmo `fetch` do cliente da API, então os erros também chegam
 * como `ErroDaApi` (com o `authCode` do backend) e cada chamada leva `X-Request-Id`.
 *
 * Não usamos os hooks de sessão do Better Auth: o estado da sessão é o `GET /api/v1/me` no
 * TanStack Query (docs/02, "Sessão e empresa ativa"). O plugin `organization` serve só para a troca
 * de empresa (`set-active`, spec 003).
 */
export function criarClienteDeAuth(opcoes: { baseUrl?: string; buscar?: Buscador } = {}) {
  silenciarAvisoEntreAbas();
  const buscar = opcoes.buscar ?? criarBuscador();
  return createAuthClient({
    baseURL: opcoes.baseUrl ?? window.location.origin,
    basePath: '/api/auth',
    plugins: [twoFactorClient(), organizationClient()],
    sessionOptions: { refetchOnWindowFocus: false },
    fetchOptions: {
      customFetchImpl: (entrada, init) => buscar(new Request(entrada, init)),
      throw: true,
    },
  });
}

export type ClienteDeAuth = ReturnType<typeof criarClienteDeAuth>;
